import React, { useRef, useEffect, useState, useContext } from "react"

import { isTextSelection } from "@tiptap/core"
import { FloatingMenu, Editor } from "@tiptap/react"
import { EditorContent, useEditor } from "./editor/react_override"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import Underline from "@tiptap/extension-underline"
import TaskList from "@tiptap/extension-task-list"
import TaskItem from "@tiptap/extension-task-item"
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight"
import { v4 as uuidv4 } from "uuid"
import { lowlight } from "lowlight/lib/core"

import { PageLink, cleanUpPageLinks } from "./editor/page_link"
import lowlightSetup from "./editor/lowlight_setup"
lowlightSetup()
import { uuid, Idea, ID } from "../../../../../model/types"
import * as Commands from "../../../../../model/commands"
import { useNana } from "../../../../hooks/useNana"
import LinkBubble from "./link_bubble"
import PageLinkPopup from "./editor/page_link_popup"
import NewBlockMenu from "./editor/new_block_menu"
import FormattingMenu from "./editor/formatting_menu"
import { MathBlock, MathInline } from "./editor/maths"
import { BubbleMenu } from "./editor/bubble_menu/react"
import { Image, fetchImageSignedUrlsFromContent, store } from "./editor/image"
import { store as imageStore } from "./editor/image"
import Hub from "../../../../../hub"

const editorBaseClasses =
  "grow min-w-0 overflow-y-auto break-words prose dark:prose-invert prose-h1:text-3xl prose-h1:font-bold prose-blockquote:font-normal focus:outline-none w-full max-w-none"

const editorSurfaceClasses = {
  solid: "bg-white dark:bg-gray-800",
  /** Transparent — parent sheet provides the paper fill (idea pane). */
  paper: "bg-transparent",
} as const

type EditorSurface = keyof typeof editorSurfaceClasses

const editorClassFor = (
  surface: EditorSurface,
  tightPadding?: boolean
): string => {
  // Idea pane (paper): keep prose-base on mobile so body doesn't shrink harder
  // than the title. Capture/sources (solid) stay denser on small screens.
  const proseSize =
    surface === "paper" ? " prose-base" : " prose-sm md:prose-base"
  const padding = tightPadding
    ? " px-2 py-2"
    : surface === "paper"
      ? " px-5 pb-6 pt-2 md:px-7 md:pb-8 md:pt-3"
      : " px-8 py-8"
  return (
    editorBaseClasses +
    proseSize +
    " " +
    editorSurfaceClasses[surface] +
    padding
  )
}

// Must always be wrapped in a div that is shown and hidden the same as this component
// or tippy stuff causes React to flip out
const ShowEditor = ({
  objectKey,
  initialContent,
  selectionPos,
  initialScrollTop,
  onUpdate,
  recreateWhen,
  onKeyDown,
  onPageLinkClick,
  onScroll,
  placeholderText,
  tightPadding,
  surface = "solid",
}: {
  objectKey: ID | null
  initialContent: string
  selectionPos?: number | null
  initialScrollTop?: number
  onUpdate: (content: string, selectionPos: number) => void
  recreateWhen?: (hub: Hub, recreate: () => void) => string[]
  onKeyDown?: (event: KeyboardEvent) => void
  onScroll?: (scrollTop: number) => void
  onPageLinkClick: (idea: Idea) => void
  placeholderText: string
  tightPadding?: boolean
  /** solid = opaque white (capture/sources); paper = transparent over frosted sheet */
  surface?: EditorSurface
}) => {
  const { model, hub } = useNana()

  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const mathsEditPopupRef = useRef<HTMLDivElement | null>(null)
  const editorRef = useRef<Editor | null>(null)
  const [suggestionHooks, _] = useState({})
  const [tick, setTick] = useState(0)

  const suggestion = {
    allowSpaces: true,
    render: () => {
      return suggestionHooks
    },
  }

  const handleNewFile = (file: File, pos: number) => {
    const uuid = uuidv4()
    store.parseImageFile(file, ({ blob, error }) => {
      if (error) imageStore.setImageState(uuid, { type: "empty", error: error })
      else if (blob) imageStore.gotFile(uuid, blob, file)
      editorRef.current?.commands.setImage({ uuid, pos })
    })
  }

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          codeBlock: false,
        }),
        Link.configure({
          openOnClick: true,
          HTMLAttributes: {
            rel: null,
          },
        }),
        Underline.configure({
          HTMLAttributes: {},
        }),
        TaskItem.configure({
          nested: true,
        }),
        TaskList,
        Placeholder.configure({
          placeholder: placeholderText,
        }),
        CodeBlockLowlight.configure({
          lowlight,
        }),
        PageLink.configure({
          HTMLAttributes: {},
          suggestion,
        }),
        MathBlock.configure({
          targetRef: mathsEditPopupRef,
        }),
        MathInline.configure({
          targetRef: mathsEditPopupRef,
        }),
        Image.configure({}),
      ],
      editorProps: {
        attributes: {
          class: editorClassFor(surface, tightPadding),
        },
        handleDOMEvents: {
          keydown: (_view, event) => {
            if (onKeyDown) onKeyDown(event)
            return false
          },
        },

        handleDrop: function (view, event, slice, moved) {
          if (
            !moved &&
            event.dataTransfer &&
            event.dataTransfer.files &&
            event.dataTransfer.files[0]
          ) {
            const coordinates = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            })
            if (event.dataTransfer && coordinates)
              handleNewFile(event.dataTransfer.files[0], coordinates.pos)
            return true
          }

          return false
        },

        handlePaste: function (view, event, slice) {
          const items = Array.from(event.clipboardData?.items || [])
          for (const item of items) {
            if (item.type.startsWith("image")) {
              const file = item.getAsFile()
              if (file) handleNewFile(file, view.state.selection.$anchor.pos)
              return true
            }
          }
          return false
        },
      },
      content: initialContent,
    },
    [objectKey, tick]
  )

  useEffect(() => {
    fetchImageSignedUrlsFromContent(initialContent)
  }, [initialContent])

  editorRef.current = editor

  useEffect(() => {
    if (!editor) return

    editor.on("update", ({ editor }) => {
      onUpdate(editor.getHTML(), editor.view.state.selection.$anchor.pos)
    })

    let keys: string[] = []
    if (recreateWhen) keys = recreateWhen(hub, () => setTick((x) => x + 1))

    // TODO: this doesn't work when the edit is at the end of the document...
    if (selectionPos) {
      let { node } = editor.view.domAtPos(selectionPos)
      if (node) {
        // @ts-expect-error
        if (node.constructor.name === "Text") node = node.parentElement
        // @ts-expect-error
        node.scrollIntoView(false)
      }
    }

    if (initialScrollTop) {
      wrapperRef.current?.scrollTo(0, initialScrollTop)
    }

    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [editor])

  useEffect(() => {
    if (wrapperRef.current && editor)
      cleanUpPageLinks(wrapperRef.current, editor, model)
  }, [editor, wrapperRef])

  const linkClicked = (e) => {
    const dataset = (e.target as any)?.dataset
    // TODO: follow page links should work in literature notes?
    if (dataset?.type === "page-link") {
      const id = dataset.id
      const idea = model.fetchIdea(id)
      if (idea) onPageLinkClick(idea)
    }
  }

  useEffect(() => {
    if (!wrapperRef.current) return
    wrapperRef.current.addEventListener("click", linkClicked)
    return () => {
      wrapperRef.current?.removeEventListener("click", linkClicked)
    }
  }, [wrapperRef, linkClicked])

  if (!editor) return null

  return (
    <>
      <div
        ref={wrapperRef}
        className="relative grow flex flex-col min-h-0 overflow-auto"
        onScroll={(e) => onScroll?.((e.target as HTMLDivElement).scrollTop)}
      >
        <EditorContent editor={editor} />
        <LinkBubble wrapperRef={wrapperRef} editor={editor} />
      </div>
      <FloatingMenu
        editor={editor}
        shouldShow={(props) => shouldShowFloatingMenu(props, editor)}
      >
        <NewBlockMenu editor={editor} />
      </FloatingMenu>
      <BubbleMenu
        editor={editor}
        tippyOptions={{ duration: 1, delay: 0 }}
        shouldShow={(props) => bubbleMenuShouldShowOriginal(props, editor)}
      >
        <FormattingMenu editor={editor} />
      </BubbleMenu>
      <PageLinkPopup editor={editor} suggestionHooks={suggestionHooks} />
      <div ref={mathsEditPopupRef} className="absolute z-50"></div>
    </>
  )
}

const bubbleMenuShouldShowOriginal = (
  { view, state, from, to, element },
  editor
) => {
  const { doc, selection } = state
  const { empty } = selection

  // Sometime check for `empty` is not enough.
  // Doubleclick an empty paragraph returns a node size of 2.
  // So we check also for an empty text size.
  const isEmptyTextBlock =
    !doc.textBetween(from, to).length && isTextSelection(state.selection)

  // When clicking on a element inside the bubble menu the editor "blur" event
  // is called and the bubble menu item is focussed. In this case we should
  // consider the menu as part of the editor and keep showing the menu
  const isChildOfMenu = element.contains(document.activeElement)

  const isMath =
    state.selection.$to.nodeBefore?.type.name === "mathInline" ||
    state.selection.$to.nodeBefore?.type.name === "mathBlock"

  const isImage = state.selection.$to.nodeBefore?.type.name === "image"

  const hasEditorFocus = view.hasFocus() || isChildOfMenu

  if (
    !hasEditorFocus ||
    empty ||
    isEmptyTextBlock ||
    !editor.isEditable ||
    isMath ||
    isImage
  ) {
    return false
  }

  return true
}

// mostly copied from here: https://github.com/ueberdosis/tiptap/blob/main/packages/extension-floating-menu/src/floating-menu-plugin.ts
const shouldShowFloatingMenu = ({ view, state }, editor) => {
  const { selection } = state
  const { $anchor, empty } = selection
  const isRootDepth = $anchor.depth === 1
  const isEmptyTextBlock =
    $anchor.parent.isTextblock &&
    !$anchor.parent.type.spec.code &&
    !$anchor.parent.textContent &&
    $anchor.parent.content.size === 0 // this is the only new bit, makes sure that we aren't just after an inline math block

  if (
    !view.hasFocus() ||
    !empty ||
    !isRootDepth ||
    !isEmptyTextBlock ||
    !editor.isEditable
  ) {
    return false
  }

  return true
}

export default ShowEditor
